
import { useState, useEffect, useCallback } from "react";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { useToast } from "@/hooks/use-toast";

export interface SmsProvider {
  id: string;
  provider_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useSmsProviders = () => {
  const getSupabaseClient = useSupabaseClient();
  const { toast } = useToast();
  const [providers, setProviders] = useState<SmsProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProviders = useCallback(async () => {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('sms_providers')
        .select('*')
        .order('provider_name');

      if (error) {
        console.error('Error fetching SMS providers:', error);
        setError(error.message);
        return;
      }

      setProviders(data || []);
      setError(null);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('Unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [getSupabaseClient]);

  const updateProvider = async (id: string, updates: Partial<SmsProvider>) => {
    const supabase = await getSupabaseClient();
    if (!supabase) return false;

    try {
      const { data, error } = await supabase
        .from('sms_providers')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating SMS provider:', error);
        toast({
          title: "Error",
          description: "Failed to update SMS provider",
          variant: "destructive",
        });
        return false;
      }

      // Update local state
      setProviders(prev => prev.map(p => p.id === id ? data : p));
      
      toast({
        title: "Provider Updated",
        description: `${data.provider_name} provider has been updated`,
        className: "bg-status-success text-white"
      });
      return true;
    } catch (err) {
      console.error('Unexpected error:', err);
      return false;
    }
  };

  const setActiveProvider = async (providerName: string) => {
    const supabase = await getSupabaseClient();
    if (!supabase) return false;

    try {
      // First, deactivate all providers
      await supabase
        .from('sms_providers')
        .update({ is_active: false })
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Update all

      // Then activate the selected provider
      const { error } = await supabase
        .from('sms_providers')
        .update({ is_active: true })
        .eq('provider_name', providerName);

      if (error) {
        console.error('Error setting active provider:', error);
        toast({
          title: "Error",
          description: "Failed to set active SMS provider",
          variant: "destructive",
        });
        return false;
      }

      // Refresh the providers list
      await fetchProviders();
      
      toast({
        title: "Active Provider Set",
        description: `${providerName} is now the active SMS provider`,
        className: "bg-status-success text-white"
      });
      return true;
    } catch (err) {
      console.error('Unexpected error:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  return {
    providers,
    loading,
    error,
    updateProvider,
    setActiveProvider,
    refetchProviders: fetchProviders
  };
};
